import type React from "react";
import { useEffect, useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../store";
import { fetchUsers } from "../store/features/userSlice";
import { createUserApi, resetUserPasswordApi } from "../api/usersApi";
import UserTable from "../components/users/UserTable";
import CreateUserForm from "../components/users/CreateUserForm";
import AdminResetPasswordModal from "../components/users/AdminResetPasswordModal";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import { toastEmitter } from "../utils/toastEmitter";
import { getErrorMessage } from "../utils/errorHandling";
import { isUserAdmin } from "../types/user";

type TranslationKeys = keyof typeof translations.en;

const UsersPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, users, usersLoading, usersError } = useSelector((state: RootState) => state.user);
  const [open, setOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const { t } = useTranslation();

  const isAdmin = isUserAdmin(user);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const handleOpen = () => {
    setOpen(true);
    setAddError(null);
  };

  const handleClose = () => {
    setOpen(false);
    setAddError(null);
  };

  const handleSubmit = async (userData: {
    _id: string;
    name: string;
    role: string;
    password: string;
  }) => {
    try {
      await createUserApi(userData);
      await dispatch(fetchUsers()).unwrap();
      toastEmitter.emit(t("User created successfully" as TranslationKeys), "success");
      handleClose();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "createUser");
      setAddError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  const handleResetPassword = (userId: string) => {
    setSelectedUser(userId);
    setResetPasswordModalOpen(true);
    setResetPasswordError(null);
  };

  const handleResetPasswordSubmit = async (userId: string, newPassword: string) => {
    try {
      await resetUserPasswordApi(userId, newPassword);
      await dispatch(fetchUsers()).unwrap();
      toastEmitter.emit(t("Password reset successfully" as TranslationKeys), "success");
      setResetPasswordModalOpen(false);
      setSelectedUser(null);
      setResetPasswordError(null);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "resetPassword");
      setResetPasswordError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    }
  };

  if (usersLoading) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (usersError) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" sx={{ mt: 3 }}>
          {usersError}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t("Users" as TranslationKeys)}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          disabled={!isAdmin}
        >
          {t("Add User" as TranslationKeys)}
        </Button>
      </Box>

      <UserTable users={users} onResetPassword={handleResetPassword} />

      <CreateUserForm open={open} onClose={handleClose} onSubmit={handleSubmit} error={addError} />

      {selectedUser && (
        <AdminResetPasswordModal
          open={resetPasswordModalOpen}
          onClose={() => {
            setResetPasswordModalOpen(false);
            setSelectedUser(null);
            setResetPasswordError(null);
          }}
          userId={selectedUser}
          onSubmit={handleResetPasswordSubmit}
          error={resetPasswordError}
        />
      )}
    </Box>
  );
};

export default UsersPage;
